/**
 * Handles rectangle drag end operation
 * @param {string} id - Rectangle ID
 * @param {number} x - New x position
 * @param {number} y - New y position
 * @param {Array} rectangles - Current rectangles array
 * @param {Function} setRectangles - Function to set rectangles
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @param {Object} currentRectangle - Optional: pass current rectangle object to preserve all properties (rotation, scaleX, scaleY, etc.)
 */
export const handleRectangleDragEnd = (
  id,
  x,
  y,
  rectangles,
  setRectangles,
  saveToHistory,
  isUndoingRedoing,
  currentRectangle = null // Optional: pass current rectangle object to preserve all properties (rotation, scaleX, scaleY, etc.)
) => {
  // Safety check: ensure rectangles is an array
  if (!Array.isArray(rectangles)) {
    return;
  }

  // CRITICAL: Use functional update to get the current state, avoiding stale closure issues
  // This ensures we always work with the most up-to-date rectangles array
  setRectangles((prevRectangles) => {
    // CRITICAL: Find the current rectangle to preserve all properties
    // If currentRectangle is provided, use it; otherwise find it from the current state
    // This ensures we preserve rotation, scaleX, scaleY, and all other properties
    const existingRectangle = currentRectangle || prevRectangles.find((rect) => rect.id === id);
    
    if (!existingRectangle) {
      return prevRectangles; // Return unchanged if rectangle not found
    }

    // CRITICAL: Preserve ALL properties from the existing rectangle (rotation, scaleX, scaleY, etc.)
    // Only update x, y - everything else should be preserved
    const updatedRectangles = prevRectangles.map((rect) =>
      rect.id === id
        ? {
            ...existingRectangle, // Preserve all existing properties (rotation, scaleX, scaleY, etc.)
            x,
            y,
            _isInOffCanvasDuringDrag: undefined,
            _previewTargetStage: undefined,
            _isDragging: undefined,
          }
        : rect
    );
    
    // Debug logging to verify rotation is preserved
    const updatedRectangle = updatedRectangles.find((rect) => rect.id === id);
    if (updatedRectangle) {
    }

    // Save to history with the updated rectangles
    // CRITICAL: Call saveToHistory OUTSIDE the setRectangles callback to avoid React batching issues
    // Use setTimeout to ensure it's called after the state update completes
    if (saveToHistory) {
      // Check if we're currently undoing/redoing to prevent clearing redo stack
      if (isUndoingRedoing && isUndoingRedoing.current) {
        return updatedRectangles;
      }

      // CRITICAL: Save drag operations immediately, not when element is unselected
      // This ensures drag operations create separate history entries that can be undone independently
      // Pass the updated rectangles array as override to ensure saveToHistory gets the correct state
      // saveToHistory signature: (operationType, currentImagesOverride, currentArrowsOverride, currentDoubleSidedArrowsOverride, currentLinesOverride, currentTextsOverride, currentSelectedTextOverride, currentSelectedTextIdOverride, currentRectanglesOverride, currentOffCanvasRectanglesOverride, ...)
      // Index 8 is currentRectanglesOverride
      setTimeout(() => {
        saveToHistory("rectangle_drag_end", null, null, null, null, null, null, null, updatedRectangles);
      }, 0);
    }

    return updatedRectangles;
  });
};

/**
 * Create a function to handle rectangle drag end with bound dependencies
 * @param {Array} rectangles - Current rectangles array
 * @param {Function} setRectangles - Function to set rectangles
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @returns {Function} Function to handle rectangle drag end
 */
// Track last drag end call to prevent duplicates
let lastRectangleDragEndCall = null;
const RECTANGLE_DRAG_END_DEBOUNCE_MS = 50; // 50ms debounce window

export const createHandleRectangleDragEnd = (
  rectangles,
  setRectangles,
  saveToHistory,
  isUndoingRedoing
) => {
  return (id, x, y, currentRectangle = null) => {
    // CRITICAL: Debounce duplicate calls to prevent creating duplicate history entries
    // This handles cases where handleRectangleDragEnd is called twice (e.g., from React strict mode or multiple event handlers)
    const now = Date.now();
    const callKey = `${id}-${x}-${y}`;
    
    if (
      lastRectangleDragEndCall &&
      lastRectangleDragEndCall.key === callKey &&
      now - lastRectangleDragEndCall.timestamp < RECTANGLE_DRAG_END_DEBOUNCE_MS
    ) {
      return; // Skip duplicate call
    }
    
    // Update last call tracking
    lastRectangleDragEndCall = {
      key: callKey,
      timestamp: now,
    };
    
    return handleRectangleDragEnd(
      id,
      x,
      y,
      rectangles,
      setRectangles,
      saveToHistory,
      isUndoingRedoing,
      currentRectangle // Pass the current rectangle object to preserve all properties (rotation, scaleX, scaleY, etc.)
    );
  };
};

